import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import "./OrderHostingForm.css";
import { HiChevronDown } from "react-icons/hi";
import orderHostingImage from "../../../../../assets/images/services/development/order.png";
import { submitHostingOrder } from "../../../../../api/hostingOrdersApi";
import { getApiErrorMessage } from "../../../../../api/unwrapApiResponse";
import PhoneCountrySelect from "../../../../shared/PhoneCountrySelect/PhoneCountrySelect";
import "../../../../shared/PhoneCountrySelect/PhoneCountrySelect.css";
import {
  DEFAULT_PHONE_COUNTRY,
  formatPhoneWithDialCode,
  getCountryByCode,
} from "../../../../../data/countryPhonePrefixes";

const HOSTING_PLANS = [
  { value: "orbit", label: "Orbit" },
  { value: "nebula", label: "Nebula" },
  { value: "galaxy", label: "Galaxy" },
];

const DEFAULT_PLAN = "nebula";

function resolvePlanFromQuery(raw) {
  const value = String(raw || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
  if (!value) return DEFAULT_PLAN;
  const match = HOSTING_PLANS.find(
    (plan) => plan.value === value || plan.label.toLowerCase() === value
  );
  return match?.value ?? DEFAULT_PLAN;
}

const EMPTY_FORM = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  company: "",
  plan: DEFAULT_PLAN,
  domain: "",
  description: "",
  privacy: false,
};

const OrderHostingForm = () => {
  const [searchParams] = useSearchParams();
  const planFromQuery = resolvePlanFromQuery(searchParams.get("plan"));
  const [form, setForm] = useState(() => ({
    ...EMPTY_FORM,
    plan: planFromQuery,
  }));
  const [phoneCountry, setPhoneCountry] = useState(DEFAULT_PHONE_COUNTRY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    setForm((prev) =>
      prev.plan === planFromQuery ? prev : { ...prev, plan: planFromQuery }
    );
  }, [planFromQuery]);

  const selectedCountry = useMemo(
    () => getCountryByCode(phoneCountry),
    [phoneCountry]
  );

  const phonePlaceholder = selectedCountry
    ? `${selectedCountry.dial} (555) 000-0000`
    : "(555) 000-0000";

  function setField(name, value) {
    setForm((prev) => ({ ...prev, [name]: value }));
    setError("");
    setSuccess(false);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setSuccess(false);

    if (!form.firstName.trim() || !form.lastName.trim()) {
      setError("Please enter your first and last name.");
      return;
    }

    if (!form.email.trim()) {
      setError("Please enter your email address.");
      return;
    }

    if (!form.plan) {
      setError("Please select a hosting plan.");
      return;
    }

    if (!form.privacy) {
      setError("Please agree to the privacy policy.");
      return;
    }

    setBusy(true);
    try {
      await submitHostingOrder({
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        phone: formatPhoneWithDialCode(phoneCountry, form.phone),
        company: form.company.trim() || null,
        plan: form.plan,
        domain: form.domain.trim() || null,
        description: form.description.trim() || null,
        agreedToPrivacy: true,
      });
      setSuccess(true);
      setForm({ ...EMPTY_FORM, plan: planFromQuery });
      setPhoneCountry(DEFAULT_PHONE_COUNTRY);
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="order-hosting">
      <div className="order-hosting__inner">
        <div className="order-hosting__main">
          <h1 className="order-hosting__title">Order hosting</h1>
          <p className="order-hosting__subtitle">
            Our friendly team would love to hear from you.
          </p>

          <form className="order-hosting__form" onSubmit={handleSubmit} noValidate>
            {success ? (
              <p
                className="order-hosting__status order-hosting__status--success"
                role="status"
              >
                Thank you! Your hosting order has been submitted successfully.
              </p>
            ) : null}

            {error ? (
              <p
                className="order-hosting__status order-hosting__status--error"
                role="alert"
              >
                {error}
              </p>
            ) : null}

            <div className="order-hosting__row">
              <div className="order-hosting__field">
                <label htmlFor="oh-first-name">First name</label>
                <input
                  id="oh-first-name"
                  name="firstName"
                  type="text"
                  placeholder="First name"
                  autoComplete="given-name"
                  value={form.firstName}
                  onChange={(e) => setField("firstName", e.target.value)}
                  disabled={busy}
                  required
                />
              </div>
              <div className="order-hosting__field">
                <label htmlFor="oh-last-name">Last name</label>
                <input
                  id="oh-last-name"
                  name="lastName"
                  type="text"
                  placeholder="Last name"
                  autoComplete="family-name"
                  value={form.lastName}
                  onChange={(e) => setField("lastName", e.target.value)}
                  disabled={busy}
                  required
                />
              </div>
            </div>

            <div className="order-hosting__field">
              <label htmlFor="oh-phone">Phone number</label>
              <div className="order-hosting__phone">
                <PhoneCountrySelect
                  value={phoneCountry}
                  disabled={busy}
                  theme="quote"
                  id="oh-phone-country"
                  onChange={(code) => {
                    setPhoneCountry(code);
                    setError("");
                    setSuccess(false);
                  }}
                />
                <input
                  id="oh-phone"
                  name="phone"
                  type="tel"
                  className="order-hosting__phone-input"
                  placeholder={phonePlaceholder}
                  autoComplete="tel-national"
                  value={form.phone}
                  onChange={(e) => setField("phone", e.target.value)}
                  disabled={busy}
                />
              </div>
            </div>

            <div className="order-hosting__field">
              <label htmlFor="oh-email">Email</label>
              <input
                id="oh-email"
                name="email"
                type="email"
                placeholder="your@mail.com"
                autoComplete="email"
                value={form.email}
                onChange={(e) => setField("email", e.target.value)}
                disabled={busy}
                required
              />
            </div>

            <div className="order-hosting__field">
              <label htmlFor="oh-company">Company</label>
              <input
                id="oh-company"
                name="company"
                type="text"
                placeholder="Your company name"
                autoComplete="organization"
                value={form.company}
                onChange={(e) => setField("company", e.target.value)}
                disabled={busy}
              />
            </div>

            <div className="order-hosting__field">
              <label htmlFor="oh-plan">Hosting plan</label>
              <div className="order-hosting__select-wrap">
                <select
                  id="oh-plan"
                  name="plan"
                  className="order-hosting__select"
                  value={form.plan}
                  onChange={(e) => setField("plan", e.target.value)}
                  disabled={busy}
                  required
                >
                  {HOSTING_PLANS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <HiChevronDown
                  className="order-hosting__select-chevron"
                  aria-hidden
                />
              </div>
            </div>

            <div className="order-hosting__field">
              <label htmlFor="oh-domain">Domain name (optional)</label>
              <input
                id="oh-domain"
                name="domain"
                type="text"
                placeholder="The domain name for your business"
                autoComplete="url"
                value={form.domain}
                onChange={(e) => setField("domain", e.target.value)}
                disabled={busy}
              />
            </div>

            <div className="order-hosting__field">
              <label htmlFor="oh-description">Project description</label>
              <textarea
                id="oh-description"
                name="description"
                rows={6}
                placeholder="Tell us about your project..."
                value={form.description}
                onChange={(e) => setField("description", e.target.value)}
                disabled={busy}
              />
            </div>

            <div className="order-hosting__check">
              <input
                id="oh-policy"
                name="policy"
                type="checkbox"
                checked={form.privacy}
                onChange={(e) => setField("privacy", e.target.checked)}
                disabled={busy}
                required
              />
              <label htmlFor="oh-policy">
                You agree to our friendly <u>privacy policy</u>.
              </label>
            </div>

            <button
              type="submit"
              className="buttonclip order-hosting__submit"
              disabled={busy}
            >
              {busy ? "Submitting…" : "Order"}
            </button>
          </form>
        </div>

        <div className="order-hosting__visual" aria-hidden="true">
          <img
            src={orderHostingImage}
            alt=""
            className="order-hosting__visual-img"
          />
        </div>
      </div>
    </section>
  );
};

export default OrderHostingForm;
